import { QueryDefinition } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { TEntity } from '../../models/entity';
import { PaginatedResult } from '../../models/paginatedResult';
import RequestTreeService from '../services/requestTreeService';
import {
    FetchArg,
    QueryAction,
    TBaseQueryFn,
    TEntityDefinitions,
    TMappedEntity,
} from './queryApiCreator';
import { QueryApiService, getItemFromCacheByTagId, provideTagList } from './queryApiService';

type TreeViewListQuery<T> = {
    registerId: string | number;
    fetchParam?: FetchArg<T>;
    expandAll: boolean;
};
type GetItemChildrenQuery<T> = {
    id: number | string;
    expanded: boolean;
    fetchParam?: FetchArg<T>;
}
interface TreeDefinitions<T extends TEntity> {
    treeviewList: QueryDefinition<TreeViewListQuery<T>, TBaseQueryFn<T>, any, PaginatedResult<T>, string>;
    getItemChildren: QueryDefinition<GetItemChildrenQuery<T>, TBaseQueryFn<T>, any, PaginatedResult<T>, string>;
}

export enum TreeQueryAction {
    treeviewList = 'treeviewList',
    getItemChildren = 'getItemChildren',
}

export type TreeMappedDefinitions<T extends TEntity, U extends string> = TMappedEntity<TreeDefinitions<T>, U>;

export type TreeEntityDefenition<T extends TEntity, U extends string> = TEntityDefinitions<T, U> & TreeMappedDefinitions<T, U>;

// TODO: Fix id selector (ANDREAS)
export class TreeQueryApiService<T extends TEntity, U extends string, E extends TEntityDefinitions<T, U> = TEntityDefinitions<T, U>>
    extends QueryApiService<T, U, E & TreeEntityDefenition<T, U>> {
    constructor(service: RequestTreeService<T, U>) {
        super(service);
        this.api = this.api.injectEndpoints({
            endpoints: build => {
                return {
                    [`${TreeQueryAction.getItemChildren}${this.capitalizeTypeName}`]:
                        build.query<PaginatedResult<T>, GetItemChildrenQuery<T>>({
                            query: (arg) => ({ request: service.treeChildrens(arg.id, arg.fetchParam) }),
                            providesTags: (result) => provideTagList(result?.value, service.typeName, undefined, service.idKey),
                        }),
                    [`${TreeQueryAction.treeviewList}${this.capitalizeTypeName}`]:
                        build.query<PaginatedResult<T>, TreeViewListQuery<T>>({
                            query: (arg) => {
                                return {
                                    request: service.treeList(arg.registerId, arg.fetchParam)
                                };
                            },
                            providesTags: (result) => provideTagList(result?.value, service.typeName, undefined, service.idKey),
                        })
                };
            },
        });
    }

    get getEntityFromCacheByTagId() {
        return (id: T['id']) => getItemFromCacheByTagId<T>(this.typeName, id, [`${QueryAction.fetch}${this.capitalizeTypeName}`, `${TreeQueryAction.treeviewList}${this.capitalizeTypeName}`]);
    }
}
